import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import moment from 'moment';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import {DashboardContent} from 'components/DashboardContent/styles';
import {DashboardHeader} from 'components/DashboardHeader/styles';
import Headline from 'components/Headline/styles';
import FullScreenLoading from 'components/shared/Utils/FullScreenLoading/FullScreenLoading';
import {DashboardTitle} from 'routes/App/dashboard/styles';
import {
  ByocPromoAction,
  ByocPromoBody,
  ByocPromoCard,
  ByocPromoImage,
  DailySnapshotChannelSelect,
  DailySnapshotHeader,
  MainPageBlock,
  MainPageCardButton,
  MainPageContainer,
  MainPageContainerLeftBlock,
  MainPageContainerRightBlock,
  MainPageContainerSubText,
  MainPageContainerText,
  MainPageContainerTitle,
  MainPageContainerTitleBlock,
  MainPageLine,
  ReferFriendAction,
  ReferFriendBody,
  ReferFriendCard,
  ReferFriendImage,
  TopBadge,
  TopCustomerAvatar,
  TopCustomerAvgOrder,
  TopCustomerCardBody,
  TopCustomerCardRow,
  TopDishesDivider,
  TopDishesGrid,
  TopDishesHeader,
  TopDishesName,
  TopDishesValue,
  ViewAllRow,
  ViewAllRowLeft
} from 'routes/App/MainPage/styles';
import IconActiveOrders from 'assets/mainPage/active_orders.svg';
import IconCustomers from 'assets/mainPage/customers-icon.svg';
import IconGrowths from 'assets/mainPage/growth-icon.svg';
import IconStats from 'assets/mainPage/stats-icon.svg';
import IconTodayOrders from 'assets/mainPage/today-orders.svg';
import IconTotalOrders from 'assets/mainPage/total-orders.svg';
import IconTodaySales from 'assets/mainPage/today-sales.svg';
import IconTotalSales from 'assets/mainPage/total-sales.svg';
import IconTotalCustomers from 'assets/mainPage/total-customers.svg';
import ImageCommissionSaved from 'assets/mainPage/commission-saved-img.svg';
import ImagePartner from 'assets/mainPage/partners-img.svg';
import ImageRefer from 'assets/mainPage/refer-friend-img.svg';
import ImageByocPromo from 'assets/mainPage/byoc-banner-img.svg';
import ImageCustomerSearch from 'assets/mainPage/customer-search-img.svg';
import IconArrow from 'assets/IconArrow';
import {FEATURE_RESTRICTION_IDS} from 'helpers/constants/restrictions';
import {showScraperPricingAlert} from 'helpers/functionUtils/growthModules';
import {
  DAILY_SNAPSHOT_CHANNELS,
  DAILY_SNAPSHOT_CHANNEL_OPTIONS,
  resolveDailySnapshotOrders,
  resolveDailySnapshotSales,
  resolveSuccessMetric
} from 'helpers/constants/analytics';
import {ButtonInfo, ButtonOutline} from 'components/buttons';
import {
  CLEAR_MAIN_PAGE_DATA,
  GET_MANAGE_PRICE_URL,
  GET_SURVEY_STATS,
  GET_SURVEY_STATS_THIS_WEEK,
  GET_TOP_CUSTOMERS,
  SET_OPEN_MODAL,
  GET_ANALYTICS_INFO
} from 'store/mainPage/action';
import {
  CashbackWidget,
  DiscountsWidget,
  GrowthProgramsPanel
} from 'routes/App/MainPage/Components/GrowthPrograms';
import TrafficBreakdown from 'routes/App/MainPage/Components/TrafficBreakdown';
import CustomerReviews from 'routes/App/MainPage/Components/CustomerReviews';
import {
  SET_REPORT_TYPE,
  SET_DATEPICKER_INDEX,
  SET_TIME_PERIOD,
  SET_REPORTS_DATE_INTERVAL
} from 'store/reports/action';
import {REPORT_TYPES, REPORT_DATEPICKER_INDEX} from 'helpers/constants/reports';
import {formatPhoneNumber} from 'helpers/functionUtils/mainPage';
import {
  RUN_GET_ACTIVE_ORDERS_JOB,
  STOP_GET_ACTIVE_ORDERS_JOB
} from 'store/global/orders/action';
import {
  RUN_GET_ACTIVE_ORDERS_JOB as RUN_GET_ACTIVE_ORDERS_JOB_V2,
  STOP_GET_ACTIVE_ORDERS_JOB as STOP_GET_ACTIVE_ORDERS_JOB_V2
} from 'store/global/ordersV2/action';
import {GET_FEEDBACK_SUMMARY} from 'store/feedback/action';
import {INSIGHTS_GROWTH_OPEN_CONTACT_US_MODAL} from 'store/insights/growth/action';
import ContactUsModal from 'routes/App/Insights/Growth/Modals/ContactUsModal';
import {INSIGHTS_GROWTH_FEATURES_INFO} from 'helpers/constants/Insights';
import SetupCallModal from 'routes/App/MainPage/Modals/SetupCallModal';
import {IconArrowDown} from 'assets/IconArrowDown';
import {isCashbackLimitedTimeLocation} from 'helpers/functionUtils/cashbackLimitedTime';

const MainPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {first_name, last_name} = useSelector((state) => state.user.userData);
  const mainPageReducer = useSelector((state) => state.mainPageReducer);
  const serviceFeatures = useSelector(
    (state) => state.user.activeLocation.AllowedServiceFeatures
  );
  const activeLocation = useSelector((state) => state.user.activeLocation);
  const showCashbackLimitedTimeOffer = isCashbackLimitedTimeLocation(
    activeLocation
  );
  const isOrdersV2 =
    serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.ORDERS_V2) ||
    serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.SHOW_BYOC_EVENTS);
  const activeOrdersCount = useSelector((state) =>
    isOrdersV2
      ? state.activeOrdersV2Reducer.activeOrdersCount
      : state.activeOrdersReducer.activeOrdersCount
  );
  const isByocOnly = useSelector(
    (state) =>
      state.user.activeLocation?.IsByocOnly === true ||
      state.user.activeLocation?.isByocOnly === true
  );

  const {
    loadingTopCustomers,
    openModal,
    loadingAnalyticsInfo,
    loadingGrowthInfo,
    analyticsInfo,
    topCustomers,
    surveyStats,
    surveyStatsThisWeek,
    growthModules
  } = mainPageReducer;

  const modalInfo = useSelector(
    (state) => state.insightsGrowthReducer.modalInfo
  );

  useEffect(() => {
    if (activeLocation._id) {
      if (
        !isOrdersV2 &&
        serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.ORDERS)
      ) {
        dispatch(STOP_GET_ACTIVE_ORDERS_JOB());
        dispatch(RUN_GET_ACTIVE_ORDERS_JOB());
      }
      if (isOrdersV2) {
        dispatch(STOP_GET_ACTIVE_ORDERS_JOB_V2());
        dispatch(RUN_GET_ACTIVE_ORDERS_JOB_V2());
      }
    }
    if (serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.ANALYTICS)) {
      dispatch(GET_ANALYTICS_INFO());
    }
    if (serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.CUSTOMERS_REPORTS)) {
      dispatch(GET_TOP_CUSTOMERS());
    }
    if (serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.INSIGHTS_GROWTH)) {
      dispatch(GET_MANAGE_PRICE_URL());
    }
    if (serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.FEEDBACK)) {
      dispatch(GET_SURVEY_STATS());
      dispatch(GET_SURVEY_STATS_THIS_WEEK());
      dispatch(GET_FEEDBACK_SUMMARY());
    }
    return () => {
      dispatch(CLEAR_MAIN_PAGE_DATA());
    };
  }, [activeLocation._id]);

  const getPercentChange = (current, previous) => {
    const today = Number(current);
    const yesterday = Number(previous);

    if (Number.isNaN(today) || Number.isNaN(yesterday)) return 0;

    if (yesterday === 0 && today > 0) return 100;

    if (yesterday === 0 && today === 0) return 0;

    return Math.round(((today - yesterday) / yesterday) * 100);
  };

  const openContactUsModal = (feature) => {
    dispatch(INSIGHTS_GROWTH_OPEN_CONTACT_US_MODAL({isOpen: true, feature}));
  };

  const [dailySnapshotChannel, setDailySnapshotChannel] = useState(
    DAILY_SNAPSHOT_CHANNELS.ALL
  );
  useEffect(() => {
    setDailySnapshotChannel(DAILY_SNAPSHOT_CHANNELS.ALL);
  }, [activeLocation._id]);
  const hasByoc = serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.BYOC);
  const isDirectOnly = !hasByoc;
  const showByocPromo =
    isDirectOnly &&
    dailySnapshotChannel === DAILY_SNAPSHOT_CHANNELS.SELF_DELIVERY;

  const dailySnapshotOrders = resolveDailySnapshotOrders(
    analyticsInfo,
    dailySnapshotChannel,
    isDirectOnly
  );
  const dailySnapshotSales = resolveDailySnapshotSales(
    analyticsInfo,
    dailySnapshotChannel,
    isDirectOnly
  );
  const dailySnapshotSelectedLabel =
    DAILY_SNAPSHOT_CHANNEL_OPTIONS.find(
      (option) => option.value === dailySnapshotChannel
    )?.label ?? 'Combined';

  const [successMetricsChannel, setSuccessMetricsChannel] = useState(
    DAILY_SNAPSHOT_CHANNELS.ALL
  );
  useEffect(() => {
    setSuccessMetricsChannel(DAILY_SNAPSHOT_CHANNELS.ALL);
  }, [activeLocation._id]);
  const successMetricsOrders = resolveSuccessMetric(
    analyticsInfo,
    successMetricsChannel,
    isDirectOnly,
    'orders'
  );
  const successMetricsSales = resolveSuccessMetric(
    analyticsInfo,
    successMetricsChannel,
    isDirectOnly,
    'totalSum'
  );
  const successMetricsCustomers = resolveSuccessMetric(
    analyticsInfo,
    successMetricsChannel,
    isDirectOnly,
    'customers'
  );
  const successMetricsCommissionSaved = resolveSuccessMetric(
    analyticsInfo,
    successMetricsChannel,
    isDirectOnly,
    'commissionSaved'
  );
  const successMetricsSelectedLabel =
    DAILY_SNAPSHOT_CHANNEL_OPTIONS.find(
      (option) => option.value === successMetricsChannel
    )?.label ?? 'Combined';

  const handleByocPromoContactUs = () => {
    if (window?.posthog) {
      window.posthog.capture('click_byoc_contact_us', {
        location_id: activeLocation?._id
      });
    }
    openContactUsModal('byoc');
  };

  return (
    <>
      {loadingAnalyticsInfo || loadingGrowthInfo ? (
        <FullScreenLoading />
      ) : (
        <DashboardContent className='DashboardContent'>
          <DashboardHeader>
            <Headline className='mTop5'>
              Hello
              {(typeof first_name === 'string' ||
                typeof last_name === 'string') &&
                ', '}
              {first_name} 👋
            </Headline>
          </DashboardHeader>
          {activeOrdersCount > 0 && (
            <MainPageBlock className='mTop20 compact'>
              <MainPageContainer className='oneLine spaceBetween'>
                <MainPageContainerTitle className='withIcon font14'>
                  <img src={IconActiveOrders} alt='Active Orders' />
                  {`${activeOrdersCount} Active Order${
                    activeOrdersCount > 1 ? 's' : ''
                  }`}
                </MainPageContainerTitle>
                <ButtonOutline
                  onClick={() => {
                    history.push(isOrdersV2 ? '/orders-v2' : '/orders');
                  }}
                  className='ButtonSmallXs'
                >
                  View and Manage
                </ButtonOutline>
              </MainPageContainer>
            </MainPageBlock>
          )}

          <DailySnapshotHeader>
            <DashboardTitle className='WithLine2px title24 titleBold'>
              Daily Snapshot
            </DashboardTitle>
            {serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.ANALYTICS) && (
              <DailySnapshotChannelSelect className='Say2EatDrop DropPeriod'>
                <FormControl fullWidth variant='outlined'>
                  <InputLabel
                    id='daily-snapshot-channel-select-label'
                    className='customRangeLabel'
                  >
                    {dailySnapshotSelectedLabel}
                  </InputLabel>
                  <Select
                    labelId='daily-snapshot-channel-select-label'
                    value={dailySnapshotChannel}
                    onChange={(event) =>
                      setDailySnapshotChannel(event.target.value)
                    }
                    inputProps={{'aria-label': 'Daily snapshot channel'}}
                    renderValue={() => ''}
                    MenuProps={{
                      anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
                      transformOrigin: {vertical: 'top', horizontal: 'left'},
                      getContentAnchorEl: null
                    }}
                  >
                    {DAILY_SNAPSHOT_CHANNEL_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        className='reportTypeItem'
                        disableRipple
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </DailySnapshotChannelSelect>
            )}
          </DailySnapshotHeader>

          {serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.ANALYTICS) && (
            <>
              <MainPageBlock className='grid2 compact'>
                <MainPageContainer>
                  <MainPageContainerTitle className='withIcon'>
                    <img src={IconTodayOrders} alt='Insights Growth' />
                    Yesterday&apos;s Orders
                  </MainPageContainerTitle>
                  {dailySnapshotOrders && dailySnapshotOrders > 0 ? (
                    <MainPageContainerText className='font42 mTop11 alignCenter'>
                      <div>
                        {new Intl.NumberFormat('en-GB', {
                          maximumFractionDigits: 0
                        }).format(dailySnapshotOrders)}
                      </div>
                    </MainPageContainerText>
                  ) : (
                    <MainPageContainerText className='noOrders'>
                      No new orders (just yet!)
                    </MainPageContainerText>
                  )}
                  <ButtonInfo className='OutlineBig ButtonInfoDrop ButtonInfoInsightsPerformance'>
                    <div className='DropInfo DropInfoMainPage'>
                      Shows yesterday&apos;s total orders. Daily shifts reflect
                      normal business patterns such as holidays, weather, local
                      events, or temporary store closures.
                    </div>
                  </ButtonInfo>
                </MainPageContainer>

                <MainPageContainer>
                  <MainPageContainerTitle className='withIcon'>
                    <img src={IconTodaySales} alt='Insights Growth' />
                    Yesterday&apos;s Sales
                  </MainPageContainerTitle>
                  {dailySnapshotSales && Number(dailySnapshotSales) > 0 ? (
                    <MainPageContainerText className='font42 mTop11 alignCenter'>
                      <div>
                        $
                        {new Intl.NumberFormat('en-GB', {
                          maximumFractionDigits: 0
                        }).format(dailySnapshotSales)}
                      </div>
                    </MainPageContainerText>
                  ) : (
                    <MainPageContainerText className='noOrders'>
                      No new orders (just yet!)
                    </MainPageContainerText>
                  )}
                  <ButtonInfo className='OutlineBig ButtonInfoDrop ButtonInfoInsightsPerformance'>
                    <div className='DropInfo DropInfoMainPage'>
                      Shows yesterday&apos;s total sales. Daily shifts reflect
                      normal business patterns such as holidays, weather, local
                      events, or temporary store closures.
                    </div>
                  </ButtonInfo>
                </MainPageContainer>
              </MainPageBlock>

              {showByocPromo && (
                <ByocPromoCard>
                  <ByocPromoImage>
                    <img src={ImageByocPromo} alt='Sauce Self Delivery' />
                  </ByocPromoImage>
                  <ByocPromoBody>
                    <div className='title'>You could be saving on delivery</div>
                    <div className='text'>
                      With Sauce Self Delivery, restaurants reduce third-party
                      delivery costs, simplify operations, and keep more revenue
                      without sacrificing order volume.
                    </div>
                  </ByocPromoBody>
                  <ByocPromoAction>
                    <ButtonOutline
                      onClick={handleByocPromoContactUs}
                      className='ButtonSmallXs Orange'
                    >
                      Contact us
                    </ButtonOutline>
                  </ByocPromoAction>
                </ByocPromoCard>
              )}
            </>
          )}

          {(() => {
            const showGoogleCard =
              (!growthModules?.googleMarketing ||
                growthModules?.googleMarketing?.status === 'Disabled') &&
              !!growthModules?.googleMarketing?.googleListingOptimizationUrl;
            const showPricingCard = showScraperPricingAlert(
              serviceFeatures,
              growthModules?.managePrices
            );
            if (!showGoogleCard && !showPricingCard) return null;
            return (
              <>
                <DashboardTitle className='WithLine2px title24 titleBold MainPageTitle'>
                  Needs Your Attention
                </DashboardTitle>
                {showGoogleCard && (
                  <MainPageBlock className='mTop16 grid1'>
                    <MainPageContainer className='flex oneLine spaceBetween'>
                      <MainPageContainerLeftBlock>
                        <div>
                          <MainPageContainerTitle className='withLabel'>
                            You&apos;re losing Google orders to third parties{' '}
                            <span className='label red'>ACTION NEEDED</span>
                          </MainPageContainerTitle>
                          <MainPageContainerText className='font12 mTop6 withButton'>
                            Your Google listing exclusivity changed. Third-party
                            orders are being placed instead of direct ones —
                            costing you margin on every order.
                          </MainPageContainerText>
                          <MainPageContainerSubText>
                            <IconArrow />
                            Impacting your revenue
                          </MainPageContainerSubText>
                        </div>
                      </MainPageContainerLeftBlock>
                      <ButtonOutline
                        onClick={() => {
                          const url =
                            growthModules?.googleMarketing
                              ?.googleListingOptimizationUrl;
                          if (url) window.open(url, '_blank');
                        }}
                        className='ButtonSmallXs AttentionAction'
                      >
                        Fix Google ordering
                      </ButtonOutline>
                    </MainPageContainer>
                  </MainPageBlock>
                )}

                {showPricingCard && (
                  <MainPageBlock className='mTop16 grid1'>
                    <MainPageContainer className='flex oneLine spaceBetween'>
                      <MainPageContainerLeftBlock>
                        <div>
                          <MainPageContainerTitle className='withLabel'>
                            Some menu prices are higher than delivery apps{' '}
                            <span className='label red'>ACTION NEEDED</span>
                          </MainPageContainerTitle>
                          <MainPageContainerText className='font12 mTop6 withButton'>
                            We found items on your direct menu priced above
                            third-party delivery apps. Matching or beating those
                            prices helps convert customers to order direct.
                          </MainPageContainerText>
                          <MainPageContainerSubText>
                            <IconArrow />
                            Could hurt direct order conversion
                          </MainPageContainerSubText>
                        </div>
                      </MainPageContainerLeftBlock>
                      <ButtonOutline
                        onClick={() => {
                          const url =
                            growthModules?.managePrices?.priceComparisonUrl;
                          if (url) window.open(url, '_blank');
                        }}
                        className='ButtonSmallXs AttentionAction'
                      >
                        Review pricing gaps
                      </ButtonOutline>
                    </MainPageContainer>
                  </MainPageBlock>
                )}
              </>
            );
          })()}

          {!activeLocation?.IsComoLocation && (
            <>
              <DashboardTitle className='WithLine2px title24 titleBold MainPageTitle'>
                Growth Programs
              </DashboardTitle>

              {analyticsInfo?.cashbacks?.isEnabled ? (
                <div className='mTop16'>
                  <GrowthProgramsPanel
                    activeCount={
                      analyticsInfo?.discounts?.activeDiscountsNumber
                    }
                    isEnrolled
                    redeemedOrdersTotal={
                      analyticsInfo?.cashbacks?.redeemedCredits?.ordersTotal
                    }
                    discountsOrdersRedeemed={
                      analyticsInfo?.discounts?.redeemed?.ordersTotal
                    }
                    discountsOrdersRedeemedTrend={
                      analyticsInfo?.discounts?.redeemed?.deltaPercent
                    }
                    ordersEarned={analyticsInfo?.cashbacks?.earned?.ordersTotal}
                    ordersEarnedTrend={
                      analyticsInfo?.cashbacks?.earned?.deltaPercent
                    }
                    ordersRedeemed={
                      analyticsInfo?.cashbacks?.redeemedCredits?.ordersTotal
                    }
                    ordersRedeemedTrend={
                      analyticsInfo?.cashbacks?.redeemedCredits?.deltaPercent
                    }
                  />
                </div>
              ) : (
                <MainPageBlock className='mTop16 grid2 noStretchSingle equalCards'>
                  <DiscountsWidget
                    activeCount={
                      analyticsInfo?.discounts?.activeDiscountsNumber
                    }
                    ordersRedeemed={
                      analyticsInfo?.discounts?.redeemed?.ordersTotal
                    }
                    ordersRedeemedTrend={
                      analyticsInfo?.discounts?.redeemed?.deltaPercent
                    }
                  />
                  <CashbackWidget
                    isEnrolled={false}
                    showLimitedTimeOffer={showCashbackLimitedTimeOffer}
                    redeemedOrdersTotal={0}
                    ordersEarned={0}
                    ordersEarnedTrend={null}
                    ordersRedeemed={0}
                    ordersRedeemedTrend={null}
                  />
                </MainPageBlock>
              )}

              <ViewAllRow>
                <ViewAllRowLeft className='darkIcon'>
                  <img src={IconGrowths} alt='Growth Actions' />
                  <span>View all growth actions</span>
                </ViewAllRowLeft>
                <ButtonOutline
                  onClick={() => {
                    history.push('/insights/growth');
                  }}
                  className='ButtonSmallXs'
                >
                  View all
                </ButtonOutline>
              </ViewAllRow>
            </>
          )}

          {serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.FEEDBACK) && (
            <CustomerReviews
              reviewsThisWeek={
                surveyStatsThisWeek?.AllСompletedSurveysCount || 0
              }
              reviewsAllTime={surveyStats?.AllСompletedSurveysCount || 0}
              averageRating={surveyStats?.AverageSatisfactionRate || 0}
            />
          )}

          {serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.ANALYTICS) &&
            !isByocOnly &&
            activeLocation?.Type === 'Regular' && <TrafficBreakdown />}

          {serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.ANALYTICS) && (
            <>
              <DashboardTitle className='WithLine2px title24 titleBold MainPageTitle'>
                Top Sellers
              </DashboardTitle>

              <MainPageBlock className='mTop16 grid2'>
                <MainPageContainer>
                  <MainPageContainerTitle>
                    Your Top Dishes
                  </MainPageContainerTitle>
                  <TopDishesGrid>
                    <TopDishesHeader>Orders</TopDishesHeader>
                    <TopDishesHeader>Sales</TopDishesHeader>
                    {analyticsInfo?.salesByItems?.map((item, index) => (
                      <React.Fragment key={item?.name}>
                        {index > 0 && <TopDishesDivider />}
                        <TopDishesName>
                          <span className='bold'>{index + 1}.</span>{' '}
                          {item?.name}
                        </TopDishesName>
                        <TopDishesValue>
                          {new Intl.NumberFormat('en-GB', {
                            maximumFractionDigits: 0
                          }).format(item?.totalOrders)}
                        </TopDishesValue>
                        <TopDishesValue>
                          {new Intl.NumberFormat('en-US', {
                            notation: 'standard',
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                            style: 'currency',
                            currency: 'USD'
                          }).format(item?.totalSales)}
                        </TopDishesValue>
                      </React.Fragment>
                    ))}
                  </TopDishesGrid>
                </MainPageContainer>

                <MainPageContainer className='flex withImg'>
                  <MainPageContainerLeftBlock className='withImg'>
                    <div>
                      <MainPageContainerTitle>
                        See what your customers are searching for
                      </MainPageContainerTitle>
                      <MainPageContainerText className='font12 mTop6 withImg'>
                        Get a report with your top search terms, most-found
                        items, and the searches that came up empty — so you know
                        exactly what to add or promote.
                      </MainPageContainerText>
                    </div>
                    <MainPageCardButton>
                      <span
                        onClick={() => {
                          window.open(
                            'https://share.hsforms.com/1wmHH4W0uTc6bApe6DcdvkA21htm',
                            '_blank'
                          );
                        }}
                      >
                        Request search report
                      </span>
                    </MainPageCardButton>
                  </MainPageContainerLeftBlock>
                  <MainPageContainerRightBlock className='withImg'>
                    <img src={ImageCustomerSearch} alt='Customer Search' />
                  </MainPageContainerRightBlock>
                </MainPageContainer>
              </MainPageBlock>
            </>
          )}

          {serviceFeatures?.includes(
            FEATURE_RESTRICTION_IDS.CUSTOMERS_REPORTS
          ) &&
            topCustomers?.length > 0 &&
            (() => {
              const currency = (n) =>
                new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 2
                }).format(Number(n || 0));

              const getInitials = (name) => {
                if (!name) return '?';
                const parts = name.trim().split(/\s+/);
                if (parts.length === 1) {
                  return parts[0].slice(0, 2).toUpperCase();
                }
                return `${parts[0][0]}${
                  parts[parts.length - 1][0]
                }`.toUpperCase();
              };

              return (
                <>
                  <DashboardTitle className='WithLine2px title24 titleBold MainPageTitle'>
                    Top Customers
                  </DashboardTitle>

                  <MainPageBlock className='mTop16 grid2'>
                    {topCustomers.map((c, index) => {
                      const row = Math.floor(index / 2);
                      const col = index % 2;
                      const avatarVariant =
                        (row + col) % 2 === 0 ? 'purple' : 'green';
                      return (
                        <MainPageContainer key={`${c.email}`}>
                          <TopCustomerCardRow>
                            <TopCustomerAvatar className={avatarVariant}>
                              {getInitials(c.name)}
                            </TopCustomerAvatar>
                            <TopCustomerCardBody>
                              <MainPageContainerTitleBlock>
                                <MainPageContainerTitle>
                                  {c.name}
                                  {index === 0 && <TopBadge>TOP</TopBadge>}
                                </MainPageContainerTitle>
                                <TopCustomerAvgOrder>
                                  Avg. Order:{' '}
                                  <span className='bold'>
                                    {currency(c.maxOrderAmount)}
                                  </span>
                                </TopCustomerAvgOrder>
                              </MainPageContainerTitleBlock>

                              <MainPageContainerText className='font12 fontBold colorGray mTop15'>
                                {`${c.email} | ${formatPhoneNumber(c.phone)}`}
                              </MainPageContainerText>

                              <MainPageContainerText className='font12 mTop11'>
                                Last Order:{' '}
                                <span className='bold'>
                                  {moment(c.lastOrderCreationDate).format(
                                    'MMM DD, YYYY'
                                  )}
                                </span>
                              </MainPageContainerText>
                            </TopCustomerCardBody>
                          </TopCustomerCardRow>
                        </MainPageContainer>
                      );
                    })}
                  </MainPageBlock>

                  <ViewAllRow>
                    <ViewAllRowLeft>
                      <img src={IconCustomers} alt='Customers' />
                      <span>View all customers and stats</span>
                    </ViewAllRowLeft>
                    <ButtonOutline
                      onClick={() => {
                        dispatch(
                          SET_REPORT_TYPE(REPORT_TYPES.CUSTOMERS_SUMMARY)
                        );
                        dispatch(
                          SET_DATEPICKER_INDEX(
                            REPORT_DATEPICKER_INDEX.ALL_PERIOD
                          )
                        );
                        dispatch(
                          SET_TIME_PERIOD(REPORT_DATEPICKER_INDEX.ALL_PERIOD)
                        );
                        dispatch(SET_REPORTS_DATE_INTERVAL([null, null]));
                        history.push('/reports?type=1&period=0');
                      }}
                      className='ButtonSmallXs'
                    >
                      View all
                    </ButtonOutline>
                  </ViewAllRow>
                </>
              );
            })()}

          <DailySnapshotHeader>
            <DashboardTitle className='WithLine2px title24 titleBold'>
              Your Success Metrics
            </DashboardTitle>
            {serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.ANALYTICS) && (
              <DailySnapshotChannelSelect className='Say2EatDrop DropPeriod'>
                <FormControl fullWidth variant='outlined'>
                  <InputLabel
                    id='success-metrics-channel-select-label'
                    className='customRangeLabel'
                  >
                    {successMetricsSelectedLabel}
                  </InputLabel>
                  <Select
                    labelId='success-metrics-channel-select-label'
                    value={successMetricsChannel}
                    onChange={(event) =>
                      setSuccessMetricsChannel(event.target.value)
                    }
                    inputProps={{'aria-label': 'Success metrics channel'}}
                    renderValue={() => ''}
                    MenuProps={{
                      anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
                      transformOrigin: {vertical: 'top', horizontal: 'left'},
                      getContentAnchorEl: null
                    }}
                  >
                    {DAILY_SNAPSHOT_CHANNEL_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        className='reportTypeItem'
                        disableRipple
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </DailySnapshotChannelSelect>
            )}
          </DailySnapshotHeader>

          {serviceFeatures?.includes(FEATURE_RESTRICTION_IDS.ANALYTICS) && (
            <>
              <MainPageBlock className='mTop16 grid3'>
                <MainPageContainer>
                  <MainPageContainerTitle className='withIcon'>
                    <img src={IconTotalOrders} alt='Insights Growth' />
                    Total Orders
                  </MainPageContainerTitle>
                  {successMetricsOrders && successMetricsOrders > 0 ? (
                    <MainPageContainerText className='font42 mTop11 alignCenter'>
                      <div>
                        {new Intl.NumberFormat('en-GB', {
                          maximumFractionDigits: 0
                        }).format(successMetricsOrders)}
                      </div>
                      <IconArrowDown />
                    </MainPageContainerText>
                  ) : (
                    <MainPageContainerText className='noOrders'>
                      <div>No new orders (just yet!)</div>
                    </MainPageContainerText>
                  )}
                </MainPageContainer>

                <MainPageContainer>
                  <MainPageContainerTitle className='withIcon'>
                    <img src={IconTotalSales} alt='Insights Growth' />
                    Total Sales
                  </MainPageContainerTitle>
                  {successMetricsSales && Number(successMetricsSales) > 0 ? (
                    <MainPageContainerText className='font42 mTop11 alignCenter'>
                      <div>
                        {new Intl.NumberFormat('en-US', {
                          notation: 'compact',
                          maximumFractionDigits: 2,
                          style: 'currency',
                          currency: 'USD'
                        }).format(successMetricsSales)}
                      </div>
                      <IconArrowDown />
                    </MainPageContainerText>
                  ) : (
                    <MainPageContainerText className='noOrders'>
                      No new orders (just yet!)
                    </MainPageContainerText>
                  )}
                </MainPageContainer>

                <MainPageContainer>
                  <MainPageContainerTitle className='withIcon'>
                    <img src={IconTotalCustomers} alt='Insights Growth' />
                    Total Customers
                  </MainPageContainerTitle>
                  {successMetricsCustomers && successMetricsCustomers > 0 ? (
                    <MainPageContainerText className='font42 mTop11 alignCenter'>
                      <div>
                        {new Intl.NumberFormat('en-GB', {
                          maximumFractionDigits: 0
                        }).format(successMetricsCustomers)}
                      </div>
                      <IconArrowDown />
                    </MainPageContainerText>
                  ) : (
                    <MainPageContainerText className='noOrders'>
                      No new orders (just yet!)
                    </MainPageContainerText>
                  )}
                </MainPageContainer>
              </MainPageBlock>

              <MainPageBlock className='mTop16 grid2'>
                <MainPageContainer className='flex withImg'>
                  <MainPageContainerLeftBlock className='withImg'>
                    <div>
                      <MainPageContainerTitle>
                        Commission Saved
                      </MainPageContainerTitle>
                      <MainPageContainerText className='font12 mTop6 withImg'>
                        Keep your profits commission-free — Sauce puts more
                        money back in your pocket.
                      </MainPageContainerText>
                      <MainPageContainerText className='font42 mTop11 alignCenter'>
                        <div>
                          {new Intl.NumberFormat('en-US', {
                            notation: 'compact',
                            maximumFractionDigits: 2,
                            style: 'currency',
                            currency: 'USD'
                          }).format(successMetricsCommissionSaved || 0)}
                        </div>
                      </MainPageContainerText>
                    </div>
                  </MainPageContainerLeftBlock>
                  <MainPageContainerRightBlock className='withImg'>
                    <img src={ImageCommissionSaved} alt='Commission' />
                  </MainPageContainerRightBlock>
                </MainPageContainer>
                <MainPageContainer className='flex withImg'>
                  <MainPageContainerLeftBlock className='withImg'>
                    <div>
                      <MainPageContainerTitle>
                        We&apos;re Here for You
                      </MainPageContainerTitle>
                      <MainPageContainerText className='font12 mTop6 withImg'>
                        We&apos;re your partners — have questions? Set up a call
                        to discuss growth and support.
                      </MainPageContainerText>
                    </div>
                    <MainPageCardButton>
                      <span
                        onClick={() => {
                          dispatch(SET_OPEN_MODAL(true));
                        }}
                      >
                        Set up a Call
                      </span>
                    </MainPageCardButton>
                  </MainPageContainerLeftBlock>
                  <MainPageContainerRightBlock className='withImg'>
                    <img src={ImagePartner} alt='Partner' />
                  </MainPageContainerRightBlock>
                </MainPageContainer>
              </MainPageBlock>

              <ViewAllRow>
                <ViewAllRowLeft className='darkIcon'>
                  <img src={IconStats} alt='success stats' />
                  <span>View all your success stats</span>
                </ViewAllRowLeft>
                <ButtonOutline
                  onClick={() => history.push('/analytics')}
                  className='ButtonSmallXs'
                >
                  View all
                </ButtonOutline>
              </ViewAllRow>
            </>
          )}

          <MainPageLine>
            <hr />
          </MainPageLine>
          <MainPageBlock className='mTop40'>
            <ReferFriendCard>
              <ReferFriendImage>
                <img src={ImageRefer} alt='Refer a Friend' />
              </ReferFriendImage>
              <ReferFriendBody>
                <div className='title'>Refer a Friend and Earn $1000!</div>
                <div className='text'>
                  Love Sauce? Don’t keep it to yourself. If you know another
                  restaurant that would benefit from Sauce, refer them to us and
                  you could pocket $1000!<span className='red'>*</span>
                </div>
              </ReferFriendBody>
              <ReferFriendAction>
                <ButtonOutline
                  onClick={() => {
                    window.open(
                      'https://www.getsauce.com/refer?utm_source=dashboard&utm_medium=login&utm_campaign=referral_dashboard&utm_content=sauce&_gl=1*1iimtvf*_gcl_au*NDUyMDU4NDI0LjE3NTU3MTkwOTcuMTc0MTY5MzA4OS4xNzU4NjU0MjgxLjE3NTg2NTUzOTQ.*_ga*MTYzMDQ3OTQ1Mi4xNzU1NTMxODAw*_ga_CFB0D1JZ02*czE3NTg2NTQyNzUkbzE2JGcxJHQxNzU4NjU1Mzk0JGo0JGwwJGgyMTMzMzQ3Mzcw*_ga_9FZH1D7NW4*czE3NTg2NTQyNzUkbzE2JGcxJHQxNzU4NjU1Mzk0JGo0JGwwJGgw',
                      '_blank'
                    );
                  }}
                  className='ButtonSmallXs green'
                >
                  Refer & Earn
                </ButtonOutline>
              </ReferFriendAction>
            </ReferFriendCard>
          </MainPageBlock>
        </DashboardContent>
      )}
      {modalInfo?.isOpen && (
        <ContactUsModal
          openModal={modalInfo.isOpen}
          feature={modalInfo.feature}
          featuresConst={INSIGHTS_GROWTH_FEATURES_INFO}
          closeModalAction={INSIGHTS_GROWTH_OPEN_CONTACT_US_MODAL}
          insightsType='Growth'
        />
      )}
      {openModal && <SetupCallModal openModal={openModal} />}
    </>
  );
};

export default MainPage;
